import { matchPath } from 'react-router-dom';
import { AgentPageContext, AgentPageEntity, AgentTranscriptEntry } from '../../types/agentHarness';

/**
 * Which record the user is looking at, for the page context sent with each Oscar message
 * (contract: the harness's `src/page-context.ts`).
 *
 * Each entity `type` names the id argument of an Oscar read tool (`get_work_order`,
 * `get_invoice`, …), which is how Oscar knows what to call. Buyer routes, plus the supplier routes
 * a buyer's internal team uses; Oscar is not on for third-party suppliers, whose pages are the
 * same paths but never reach this.
 */
interface OscarRoute {
	path: string;
	page: string;
	/** Entity type → route param holding its id. */
	entities: [string, string][];
}

/** Most specific first, so a sub-tab (`/details`, `/notes`, …) inherits its parent's entity. */
const OSCAR_ROUTES: OscarRoute[] = [
	{
		path: '/buyer/workOrders/detail/:workOrderId/invoices/:invoiceId',
		page: 'work_order',
		entities: [
			['work_order', 'workOrderId'],
			['invoice', 'invoiceId'],
		],
	},
	{
		path: '/buyer/workOrders/detail/:workOrderId/editProposal/:proposalId',
		page: 'work_order',
		entities: [
			['work_order', 'workOrderId'],
			['proposal', 'proposalId'],
		],
	},
	{
		path: '/buyer/workOrders/detail/:id',
		page: 'work_order',
		entities: [['work_order', 'id']],
	},
	{
		path: '/buyer/invoices/detail/:id',
		page: 'invoice',
		entities: [['invoice', 'id']],
	},
	// The quotes page id is a proposal id.
	{
		path: '/buyer/quotes/detail/:id',
		page: 'quote',
		entities: [['proposal', 'id']],
	},
	{
		path: '/buyer/requestsForProposal/detail/:rfpId/supplier/:supplierFacilityId/bid',
		page: 'rfp',
		entities: [
			['rfp', 'rfpId'],
			['supplier_facility', 'supplierFacilityId'],
		],
	},
	{
		path: '/buyer/requestsForProposal/detail/:id',
		page: 'rfp',
		entities: [['rfp', 'id']],
	},
	{ path: '/buyer/assets/detail/:id', page: 'asset', entities: [['asset', 'id']] },
	{ path: '/buyer/assets/:id', page: 'asset', entities: [['asset', 'id']] },
	{
		path: '/buyer/locations/detail/:id',
		page: 'location',
		entities: [['location', 'id']],
	},
	{
		path: '/buyer/projects/detail/:id',
		page: 'project',
		entities: [['project', 'id']],
	},
	{
		path: '/buyer/siteSurveys/walkthrough/detail/:id',
		page: 'walkthrough',
		entities: [['walkthrough', 'id']],
	},
	{
		path: '/buyer/siteSurveys/detail/:id',
		page: 'site_survey',
		entities: [['site_survey', 'id']],
	},
	// The supplier page id is a supplier facility id.
	{
		path: '/buyer/suppliers/detail/:id',
		page: 'supplier',
		entities: [['supplier_facility', 'id']],
	},
	{
		path: '/buyer/budgets/detail/:budgetId/locations/:locationId',
		page: 'budget',
		entities: [
			['budget', 'budgetId'],
			['location', 'locationId'],
		],
	},
	{
		path: '/buyer/budgets/detail/:id',
		page: 'budget',
		entities: [['budget', 'id']],
	},
	// A buyer's internal team, on the supplier side. Same entity types, so the same Oscar read tools;
	// only the pages internal teams have tools for (no budgets).
	{
		path: '/supplier/workOrders/detail/:workOrderId/invoices/:invoiceId',
		page: 'work_order',
		entities: [
			['work_order', 'workOrderId'],
			['invoice', 'invoiceId'],
		],
	},
	{
		path: '/supplier/workOrders/detail/:workOrderId/editProposal/:proposalId',
		page: 'work_order',
		entities: [
			['work_order', 'workOrderId'],
			['proposal', 'proposalId'],
		],
	},
	// `editProposalContractor` is left to the generic entry: its id is a subcontractor's quote,
	// which no Oscar tool reads, so naming it as a proposal would point Oscar at the wrong record.
	{
		path: '/supplier/workOrders/detail/:id',
		page: 'work_order',
		entities: [['work_order', 'id']],
	},
	{
		path: '/supplier/invoices/detail/:id',
		page: 'invoice',
		entities: [['invoice', 'id']],
	},
	// As on the buyer side, the quotes page id is a proposal id.
	{
		path: '/supplier/quotes/detail/:id',
		page: 'quote',
		entities: [['proposal', 'id']],
	},
	{
		path: '/supplier/requestsForProposal/detail/:id/supplier/:supplierFacilityId/bid',
		page: 'rfp',
		entities: [
			['rfp', 'id'],
			['supplier_facility', 'supplierFacilityId'],
		],
	},
	{
		path: '/supplier/requestsForProposal/detail/:id',
		page: 'rfp',
		entities: [['rfp', 'id']],
	},
	{ path: '/supplier/assets/detail/:id', page: 'asset', entities: [['asset', 'id']] },
	{
		path: '/supplier/locations/detail/:id',
		page: 'location',
		entities: [['location', 'id']],
	},
	{
		path: '/supplier/projects/detail/:id',
		page: 'project',
		entities: [['project', 'id']],
	},
	{
		path: '/supplier/siteSurveys/walkthrough/detail/:id',
		page: 'walkthrough',
		entities: [['walkthrough', 'id']],
	},
	{
		path: '/supplier/walkthroughs/detail/:id',
		page: 'walkthrough',
		entities: [['walkthrough', 'id']],
	},
	{
		path: '/supplier/siteSurveys/detail/:id',
		page: 'site_survey',
		entities: [['site_survey', 'id']],
	},
	{
		path: '/supplier/suppliers/detail/:id',
		page: 'supplier',
		entities: [['supplier_facility', 'id']],
	},
];

const NUMERIC_ID = /^\d+$/;
const MAX_PATH_LENGTH = 512;

/** What the harness accepts for `path`: starts with `/`, no whitespace or control characters. */
const safePath = (pathname: string): string | undefined => {
	if (!pathname || pathname[0] !== '/' || pathname.length > MAX_PATH_LENGTH) return undefined;
	const unsafe = Array.from(pathname).some((character) => {
		const code = character.charCodeAt(0);
		return code <= 32 || code === 127;
	});
	return unsafe ? undefined : pathname;
};

export interface OscarPageContextMatch {
	context: AgentPageContext;
}

/**
 * The page context for `pathname`, or null when the page is not about a record Oscar can read.
 * Non-numeric ids (`new`, `templates`, …) are dropped; a route left with no entity falls through
 * to the next one. Validated here so the harness never answers 400.
 */
export const oscarPageContextFor = (pathname: string): OscarPageContextMatch | null => {
	for (const route of OSCAR_ROUTES) {
		const match = matchPath<{ [param: string]: string }>(pathname, { path: route.path });
		if (!match) continue;
		const entities: AgentPageEntity[] = route.entities
			.map(([type, param]) => ({ type, id: match.params[param] }))
			.filter((entity) => NUMERIC_ID.test(entity.id || ''));
		if (entities.length === 0) continue;
		const path = safePath(pathname);
		return { context: { page: route.page, ...(path ? { path } : {}), entities } };
	}
	return null;
};

/** The context for a single record, for surfaces that name one explicitly. */
export const oscarEntityContext = (
	page: string,
	type: string,
	id: number | string,
	pathname?: string
): AgentPageContext | null => {
	const entityId = String(id);
	if (!NUMERIC_ID.test(entityId)) return null;
	const path = pathname ? safePath(pathname) : undefined;
	return { page, ...(path ? { path } : {}), entities: [{ type, id: entityId }] };
};

/** How the context bar names each entity type, e.g. "WO #34234". */
const ENTITY_LABELS: { [type: string]: string } = {
	work_order: 'WO',
	invoice: 'Invoice',
	proposal: 'Quote',
	rfp: 'RFP',
	supplier_facility: 'Supplier',
	asset: 'Asset',
	location: 'Location',
	project: 'Project',
	walkthrough: 'Walkthrough',
	site_survey: 'Site survey',
	budget: 'Budget',
};

/** "WO #34234" for one entity. */
export const oscarEntityLabel = (entity: AgentPageEntity): string =>
	`${ENTITY_LABELS[entity.type] || entity.type} #${entity.id}`;

/** The record a context is about: its first entity, or null when it names none. */
export const oscarContextEntity = (
	context: AgentPageContext | null | undefined
): AgentPageEntity | null => {
	const entities = context ? context.entities || [] : [];
	return entities.length ? entities[0] : null;
};

/**
 * The record a chat is about: the first message that shared one. A chat stays about it while the
 * user moves to other pages — an Ask Oscar chat from its first message, a chat opened from the
 * dock from the first message sent with Share on. Null while no message has shared a record.
 */
export const oscarConversationContext = (
	entries: AgentTranscriptEntry[]
): AgentPageContext | null => {
	const first = entries.find(
		(entry) => entry.kind === 'user' && !!oscarContextEntity(entry.context)
	);
	return first && first.kind === 'user' ? first.context : null;
};
